import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
  FormHelperText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  FilterList as FilterListIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { pacientesIniciais, consultasIniciais } from '../shared/initialData';

const Pacientes = () => {
  const [pacientes, setPacientes] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [psicologoId, setPsicologoId] = useState(null);
  const [filtros, setFiltros] = useState({
    status: 'todos',
    genero: 'todos'
  });
  const [mostraFiltros, setMostraFiltros] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPaciente, setCurrentPaciente] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    dataNascimento: null,
    genero: '',
    estadoCivil: '',
    profissao: '',
    historico: '',
    status: 'Ativo',
    tipoCobranca: 'consulta',
    valorCobranca: '',
    senha: '',
    confirmarSenha: ''
  });
  
  const [consultas, setConsultas] = useState([]);
  
  // Verificar o psicólogo logado e carregar seus pacientes
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('userType');
    if (tipoUsuario !== 'psicologo') {
      return;
    }

    const storedPsicologoId = localStorage.getItem('userId');
    if (storedPsicologoId) {
      setPsicologoId(parseInt(storedPsicologoId));
      
      // Filtrar pacientes do psicólogo
      const pacientesFiltrados = pacientesIniciais.filter(
        paciente => paciente.psicologoId === parseInt(storedPsicologoId)
      );
      setPacientes(pacientesFiltrados);
      
      // Carregar consultas
      const todasConsultas = consultasIniciais.filter(
        consulta => consulta.psicologoId === parseInt(storedPsicologoId)
      );
      setConsultas(todasConsultas);
    }
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleOpenDialog = (paciente = null) => {
    if (paciente) {
      // Editar paciente existente
      const dataArray = paciente.dataNascimento.split('/');
      const dataNascimento = new Date(dataArray[2], dataArray[1] - 1, dataArray[0]);
      
      setFormData({
        nome: paciente.nome,
        email: paciente.email,
        telefone: paciente.telefone,
        dataNascimento: dataNascimento,
        genero: paciente.genero,
        estadoCivil: paciente.estadoCivil,
        profissao: paciente.profissao,
        historico: paciente.historico,
        status: paciente.status,
        tipoCobranca: paciente.tipoCobranca || 'consulta',
        valorCobranca: paciente.valorCobranca || '',
        senha: '',
        confirmarSenha: ''
      });
      setCurrentPaciente(paciente);
    } else {
      // Novo paciente
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        dataNascimento: null,
        genero: '',
        estadoCivil: '',
        profissao: '',
        historico: '',
        status: 'Ativo',
        tipoCobranca: 'consulta',
        valorCobranca: '',
        senha: '',
        confirmarSenha: ''
      });
      setCurrentPaciente(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
  };
  
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Limpar erro quando o usuário corrige o campo
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: null
      });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nome.trim()) errors.nome = 'O nome é obrigatório';
    if (!formData.email.trim()) errors.email = 'O e-mail é obrigatório';
    if (!formData.telefone.trim()) errors.telefone = 'O telefone é obrigatório';
    if (!formData.dataNascimento) errors.dataNascimento = 'A data de nascimento é obrigatória';
    if (!formData.genero) errors.genero = 'O gênero é obrigatório';
    
    // Validação de senha apenas para novos pacientes
    if (!currentPaciente) {
      if (!formData.senha) errors.senha = 'A senha é obrigatória';
      if (formData.senha && formData.senha.length < 6) errors.senha = 'A senha deve ter pelo menos 6 caracteres';
      if (!formData.confirmarSenha) errors.confirmarSenha = 'Confirme a senha';
      if (formData.senha !== formData.confirmarSenha) errors.confirmarSenha = 'As senhas não coincidem';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSavePaciente = () => {
    if (!validateForm()) return;
    
    const dataFormatada = formData.dataNascimento.toLocaleDateString('pt-BR');
    const dataAtual = new Date().toLocaleDateString('pt-BR');
    
    if (currentPaciente) {
      // Atualizar paciente existente
      const pacientesAtualizados = pacientes.map(p => {
        if (p.id === currentPaciente.id) {
          return {
            ...p,
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            dataNascimento: dataFormatada,
            genero: formData.genero,
            estadoCivil: formData.estadoCivil,
            profissao: formData.profissao,
            historico: formData.historico,
            status: formData.status,
            tipoCobranca: formData.tipoCobranca,
            valorCobranca: formData.valorCobranca
          };
        }
        return p;
      });
      setPacientes(pacientesAtualizados);
    } else {
      // Criar novo paciente
      const novoPaciente = {
        id: Math.max(...pacientes.map(p => p.id), 0) + 1,
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        dataNascimento: dataFormatada,
        genero: formData.genero,
        estadoCivil: formData.estadoCivil,
        profissao: formData.profissao,
        historico: formData.historico,
        status: formData.status,
        tipoCobranca: formData.tipoCobranca,
        valorCobranca: formData.valorCobranca,
        criadoEm: dataAtual,
        ultimaConsulta: 'Nunca',
        proximaConsulta: 'Não agendada',
        psicologoId: psicologoId,
        senha: formData.senha // Armazenar a senha (em um sistema real, seria criptografada)
      };
      
      setPacientes([...pacientes, novoPaciente]);
      
      // Exibir mensagem de acesso para o paciente
      alert(`Paciente cadastrado com sucesso!\n\nInforme ao paciente que ele pode acessar o sistema com:\nE-mail: ${formData.email}\nSenha: ${formData.senha}`);
    }
    
    handleCloseDialog();
  };
  
  const handleDeletePaciente = (pacienteId) => {
    if (window.confirm('Tem certeza que deseja excluir este paciente?')) {
      setPacientes(pacientes.filter(p => p.id !== pacienteId));
    }
  };
  
  const handleChangeStatus = (pacienteId, novoStatus) => {
    const pacientesAtualizados = pacientes.map(p => {
      if (p.id === pacienteId) {
        return { ...p, status: novoStatus };
      }
      return p;
    });
    setPacientes(pacientesAtualizados);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleFilterChange = (filterType, value) => {
    setFiltros({
      ...filtros,
      [filterType]: value
    });
  };
  
  const formatarData = (data) => {
    if (!data) return '';
    try {
      const [dia, mes, ano] = data.split('/');
      return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return data;
    }
  };
  
  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return '';
    try {
      const [dia, mes, ano] = dataNascimento.split('/');
      const dataNasc = new Date(ano, mes - 1, dia);
      const hoje = new Date();
      let idade = hoje.getFullYear() - dataNasc.getFullYear();
      const mesAtual = hoje.getMonth();
      const mesNascimento = dataNasc.getMonth();
      
      if (mesNascimento > mesAtual || 
          (mesNascimento === mesAtual && dataNasc.getDate() > hoje.getDate())) {
        idade--;
      }
      
      return idade;
    } catch (error) {
      return '';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Ativo':
        return { bg: '#4caf50', color: 'white' };
      case 'Inativo':
        return { bg: '#f44336', color: 'white' };
      case 'Em Pausa':
        return { bg: '#ff9800', color: 'white' };
      case 'Novo':
        return { bg: '#2196f3', color: 'white' };
      default:
        return { bg: '#757575', color: 'white' };
    }
  };
  
  const getProximaConsulta = (pacienteId) => {
    const consultasAgendadas = consultas.filter(c => 
      c.pacienteId === pacienteId && c.status === 'Agendada'
    );
    
    if (consultasAgendadas.length === 0) return 'Não agendada';
    
    // Ordenar consultas por data
    consultasAgendadas.sort((a, b) => {
      const dataA = a.data.split('/').reverse().join('-');
      const dataB = b.data.split('/').reverse().join('-');
      return new Date(dataA) - new Date(dataB);
    });
    
    return `${consultasAgendadas[0].data} às ${consultasAgendadas[0].horario}`;
  };
  
  const getUltimaConsulta = (pacienteId) => {
    const consultasRealizadas = consultas.filter(c => 
      c.pacienteId === pacienteId && c.status === 'Realizada'
    );
    
    if (consultasRealizadas.length === 0) return 'Nunca';
    
    // Ordenar consultas por data (mais recente primeiro)
    consultasRealizadas.sort((a, b) => {
      const dataA = a.data.split('/').reverse().join('-');
      const dataB = b.data.split('/').reverse().join('-');
      return new Date(dataB) - new Date(dataA);
    });
    
    return consultasRealizadas[0].data;
  };
  
  const filteredPacientes = () => {
    return pacientes.filter(paciente => {
      // Filtro de pesquisa
      const matchesSearch = paciente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            paciente.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro de status
      const matchesStatus = filtros.status === 'todos' || paciente.status === filtros.status;
      
      // Filtro de gênero
      const matchesGenero = filtros.genero === 'todos' || paciente.genero === filtros.genero;
      
      return matchesSearch && matchesStatus && matchesGenero;
    });
  };
  
  const pacientesStatus = () => {
    if (tabValue === 0) {
      // Todos
      return filteredPacientes();
    } else if (tabValue === 1) {
      // Ativos
      return filteredPacientes().filter(p => p.status === 'Ativo');
    } else if (tabValue === 2) {
      // Inativos
      return filteredPacientes().filter(p => p.status === 'Inativo');
    }
    return filteredPacientes();
  };
  
  const toggleFiltros = () => {
    setMostraFiltros(!mostraFiltros);
  };
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Pacientes
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          variant="contained" 
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
        >
          Novo Paciente
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Buscar paciente..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Button 
            variant={mostraFiltros ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={toggleFiltros}
            sx={{ 
              borderColor: '#4a8a84', 
              color: mostraFiltros ? 'white' : '#4a8a84',
              bgcolor: mostraFiltros ? '#4a8a84' : 'transparent',
              '&:hover': { 
                bgcolor: mostraFiltros ? '#3a7a74' : 'rgba(74, 138, 132, 0.1)'
              }
            }}
          >
            Filtros
          </Button>
        </Box>
      </Box>
      
      {mostraFiltros && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filtrar Pacientes
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtros.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="Ativo">Ativos</MenuItem>
                  <MenuItem value="Inativo">Inativos</MenuItem>
                  <MenuItem value="Em Pausa">Em Pausa</MenuItem>
                  <MenuItem value="Novo">Novos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Gênero</InputLabel>
                <Select
                  value={filtros.genero}
                  label="Gênero"
                  onChange={(e) => handleFilterChange('genero', e.target.value)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="Masculino">Masculino</MenuItem>
                  <MenuItem value="Feminino">Feminino</MenuItem>
                  <MenuItem value="Não Binário">Não Binário</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { minWidth: 100 },
            '& .Mui-selected': { color: '#4a8a84' },
            '& .MuiTabs-indicator': { backgroundColor: '#4a8a84' }
          }}
        >
          <Tab label="Todos" />
          <Tab label="Ativos" />
          <Tab label="Inativos" />
        </Tabs>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de pacientes">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Idade</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Próxima Consulta</TableCell>
              <TableCell>Última Consulta</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pacientesStatus().length > 0 ? (
              pacientesStatus()
                .sort((a, b) => a.nome.localeCompare(b.nome))
                .map((paciente) => (
                  <TableRow
                    key={paciente.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {paciente.nome}
                    </TableCell>
                    <TableCell>{calcularIdade(paciente.dataNascimento)} anos</TableCell>
                    <TableCell>
                      <Typography variant="body2">{paciente.telefone}</Typography>
                      <Typography variant="body2" color="text.secondary">{paciente.email}</Typography>
                    </TableCell>
                    <TableCell>{getProximaConsulta(paciente.id)}</TableCell>
                    <TableCell>{getUltimaConsulta(paciente.id)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={paciente.status} 
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(paciente.status).bg,
                          color: getStatusColor(paciente.status).color
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(paciente)}
                            sx={{ color: '#4a8a84' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {paciente.status !== 'Ativo' && (
                          <Tooltip title="Marcar como ativo">
                            <IconButton 
                              size="small" 
                              onClick={() => handleChangeStatus(paciente.id, 'Ativo')}
                              sx={{ color: '#4caf50' }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeletePaciente(paciente.id)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum paciente encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog para criar/editar paciente */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentPaciente ? 'Editar Paciente' : 'Novo Paciente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Completo"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                error={!!formErrors.nome}
                helperText={formErrors.nome}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-mail"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.telefone}
                onChange={(e) => handleInputChange('telefone', e.target.value)}
                error={!!formErrors.telefone}
                helperText={formErrors.telefone}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                type="date"
                value={formData.dataNascimento ? formData.dataNascimento.toISOString().split('T')[0] : ''}
                onChange={(e) => handleInputChange('dataNascimento', new Date(e.target.value))}
                error={!!formErrors.dataNascimento}
                helperText={formErrors.dataNascimento}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={!!formErrors.genero}>
                <InputLabel>Gênero</InputLabel>
                <Select
                  value={formData.genero}
                  label="Gênero"
                  onChange={(e) => handleInputChange('genero', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  <MenuItem value="Masculino">Masculino</MenuItem>
                  <MenuItem value="Feminino">Feminino</MenuItem>
                  <MenuItem value="Não Binário">Não Binário</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </Select>
                {formErrors.genero && (
                  <FormHelperText>{formErrors.genero}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Estado Civil</InputLabel>
                <Select
                  value={formData.estadoCivil}
                  label="Estado Civil"
                  onChange={(e) => handleInputChange('estadoCivil', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                  <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                  <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                  <MenuItem value="Viúvo(a)">Viúvo(a)</MenuItem>
                  <MenuItem value="União Estável">União Estável</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {!currentPaciente && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Dados de Acesso
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Defina uma senha para que o paciente possa acessar a plataforma
                  </Typography>
                </Grid>
            
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Senha"
                    type="password"
                    value={formData.senha}
                    onChange={(e) => handleInputChange('senha', e.target.value)}
                    error={!!formErrors.senha}
                    helperText={formErrors.senha}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirmar Senha"
                    type="password"
                    value={formData.confirmarSenha}
                    onChange={(e) => handleInputChange('confirmarSenha', e.target.value)}
                    error={!!formErrors.confirmarSenha}
                    helperText={formErrors.confirmarSenha}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profissão"
                value={formData.profissao}
                onChange={(e) => handleInputChange('profissao', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="Ativo">Ativo</MenuItem>
                  <MenuItem value="Inativo">Inativo</MenuItem>
                  <MenuItem value="Em Pausa">Em Pausa</MenuItem>
                  <MenuItem value="Novo">Novo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Informações Financeiras
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Cobrança</InputLabel>
                <Select
                  value={formData.tipoCobranca}
                  label="Tipo de Cobrança"
                  onChange={(e) => handleInputChange('tipoCobranca', e.target.value)}
                >
                  <MenuItem value="consulta">Por Consulta</MenuItem>
                  <MenuItem value="hora">Por Hora</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Valor (${formData.tipoCobranca === 'consulta' ? 'por consulta' : 'por hora'})`}
                type="number"
                value={formData.valorCobranca}
                onChange={(e) => handleInputChange('valorCobranca', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Histórico Clínico
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.historico}
                onChange={(e) => handleInputChange('historico', e.target.value)}
                placeholder="Descreva o histórico clínico do paciente..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSavePaciente} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pacientes; 